// ProcessSteps.js
import React from 'react';
import { motion } from 'framer-motion';

const ProcessSteps = () => {
  const steps = [
    {
      number: '01',
      title: 'The First Conversation, Free of Charge',
      subtitle: 'Everything opens with a session that costs you nothing',
      points: [
        'We go through your qualifications and what you want out of studying overseas',
        'You tell us the countries and the courses you have in mind',
        'We say plainly what you are eligible for and what is out of reach',
        'We set out the requirements, the dates and what happens after'
      ],
      footer: 'You know where you stand before committing to anything.'
    },
    {
      number: '02',
      title: 'Matching You to Institutions',
      subtitle: 'With your profile in hand, the next stage is to:',
      points: [
        'Line you up with schools and programmes that fit',
        'Approach our partner institutions where that strengthens your case',
        'Look for scholarships or fee reductions you could be put forward for'
      ],
      footer: 'Every recommendation is explained to you and rests on what you qualify for.'
    },
    {
      number: '03',
      title: 'Preparing and Filing the Application',
      subtitle: 'Once a route is agreed, the application work sits with our team, which covers:',
      points: [
        'Putting the applications together and sending them in',
        'Writing and reworking your Statement of Purpose',
        'Gathering transcripts and the supporting paperwork',
        'Handling the submission with each institution'
      ],
      footer: 'We tell you where things stand as we go.'
    },
    {
      number: '04',
      title: 'You and the Institution, Speaking Directly',
      subtitle: 'From the point contact opens:',
      points: [
        'Correspondence about admission runs straight between the two of you',
        'Offer letters and any scholarship award arrive in your own name',
        'Fees for tuition and anything related are settled with the school itself'
      ],
      footer: 'Ivipass does not collect tuition on behalf of any institution. We stay on hand to advise and support you at every stage.'
    },
    {
      number: '05',
      title: 'After the Offer Arrives',
      subtitle: 'Support does not stop once you are admitted. We carry on by:',
      points: [
        'Talking you through the terms of the offer and its deadlines',
        'Walking you through acceptance and enrolment',
        'Helping you prepare for the move and for the study itself'
      ],
      footer: 'The aim is that you go into it knowing exactly what to expect.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-surface text-secondary text-sm font-light px-6 py-2 rounded-full mb-4">
            The Process
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            How a Study Abroad Application Runs
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Five stages, from the opening conversation through to the period after your offer lands
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div className="grid md:grid-cols-12 gap-6 items-start">
                  {/* Left Side - Number Only */}
                  <div className="md:col-span-2 flex items-center md:items-start">
                    <div className="flex-shrink-0">
                      <div className="text-6xl font-light text-primary/10">
                        {step.number}
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Content */}
                  <div className="md:col-span-10">
                    <div className="bg-surface p-8 rounded-lg">
                      <h3 className="text-2xl font-semibold text-primary mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 text-sm font-light mb-4">
                        {step.subtitle}
                      </p>
                      
                      {/* Points List */}
                      <ul className="space-y-2">
                        {step.points.map((point, pointIndex) => (
                          <li key={pointIndex} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            <span className="text-gray-600 text-sm font-light">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;
